import { useCallback, useEffect, useMemo, useState } from "react";
import { ICart } from "@features/cart/ICart";
import { IFlyingCart } from "@features/cart/IFlyingCart";
import { EAppointmentType, EServiceGroup } from "@constants";
import { useHistory } from "react-router-dom";
import { selectOfferInCartByServiceGroup } from "@utils/offerUtils";
import { IStepRoute } from "@common/StepperRouter";
import { useTranslation } from "react-i18next";
import { getProspectReference } from "@utils/storage";
import { format, isSameDay } from "date-fns";
import { useForm } from "react-hook-form";
import { patchAddInstallationAppointment } from "@modules/eirSalesFacade/apis/addInstallationAppointment";
import { patchUpdateActivationDate } from "@modules/eirSalesFacade/apis/updateActivationDate";
import { IInstallationAppointmentForm } from "@features/installation/IInstallationAppointmentForm";
import { mapTimeSlotCodeToLabel, mapTimeSlotLabelToCode } from "@features/installation/utils/appointmentUtils";
import { useStyle } from "./style";
import { useDeviceType } from "@hooks/useDeviceType";
import { useNoInstallSlot } from "@pages/Installation/useNoInstallSlot";
import type { INoInstallActivationForm } from "@features/installation/useInstallationNoInstall";

export interface IInstallationPageParams {
    cart: ICart;
    flyingCart: IFlyingCart;
    nextStep?: IStepRoute;
    setCart: (cart: ICart | undefined) => void;
}

export type InstallationInfoContent =
    | { kind: "preorder"; serviceDate: Date }
    | { kind: "no-install" }
    | { kind: "none" };

export const useInstallationPage = (props: IInstallationPageParams) => {
    const { cart, nextStep, setCart } = props;
    const { push } = useHistory();
    const { t } = useTranslation("installation");
    const [appointmentFormValues, setAppointmentFormValues] = useState<IInstallationAppointmentForm | null>(null);
    const noInstallFormMethods = useForm<INoInstallActivationForm>({
        defaultValues: { activationDate: null },
    });
    const { formState, reset: resetNoInstallForm } = noInstallFormMethods;
    const { dirtyFields } = formState;
    const { classes } = useStyle();
    const { isMobile } = useDeviceType();
    const { firstAvailableDate, filteredActivationDateList, isLoading, error, getActivationDates } = useNoInstallSlot();

    const broadbandOffer = useMemo(() => selectOfferInCartByServiceGroup(cart, EServiceGroup.SA_BB), [cart]);
    const prequal = broadbandOffer?.prequal;
    const appointmentType = broadbandOffer?.appointment?.type;
    const offerId = broadbandOffer?.offerId;

    const handleNextClick = useCallback(async () => {
        const prospectReference = getProspectReference();
        const offerIdLocal = broadbandOffer?.offerId;
        const appointmentTypeLocal = broadbandOffer?.appointment?.type;
        const prequalLocal = broadbandOffer?.prequal;

        if (!prospectReference || !offerIdLocal) {
            push({ pathname: nextStep?.path ?? "" });

            return;
        }

        try {
            let apiCallSuccess = false;

            if (!prequalLocal?.isPreOrder && appointmentTypeLocal === EAppointmentType.FULL_INSTALL) {
                if (!appointmentFormValues?.appointmentDate || !appointmentFormValues?.timeSlot) {
                    return;
                }

                const { appointmentDate, timeSlot } = appointmentFormValues;
                const formattedDate = format(appointmentDate, "yyyy-MM-dd");
                const timeCode = mapTimeSlotLabelToCode(timeSlot);

                const result = await patchAddInstallationAppointment(prospectReference, offerIdLocal, {
                    at: formattedDate,
                    time: timeCode,
                });

                if (result) {
                    setCart(result);
                }

                apiCallSuccess = result !== undefined;
            } else if (appointmentTypeLocal === EAppointmentType.NO_INSTALL) {
                const isValid = await noInstallFormMethods.trigger("activationDate");

                if (!isValid) {
                    return;
                }

                const activationDate = noInstallFormMethods.getValues("activationDate");

                if (!activationDate) {
                    return;
                }

                const formattedDate = format(activationDate, "yyyy-MM-dd");

                const result = await patchUpdateActivationDate(prospectReference, offerIdLocal, {
                    activationAt: formattedDate,
                });

                if (result) {
                    setCart(result);
                }

                apiCallSuccess = result !== undefined;
            } else {
                apiCallSuccess = true;
            }

            if (apiCallSuccess) {
                push({ pathname: nextStep?.path ?? "" });
            }
            // eslint-disable-next-line no-empty
        } catch {}
    }, [appointmentFormValues, broadbandOffer, nextStep?.path, noInstallFormMethods, push, setCart]);

    useEffect(() => {
        if (appointmentType !== EAppointmentType.NO_INSTALL) {
            resetNoInstallForm({ activationDate: null });

            return;
        }

        if (isLoading || error !== undefined || filteredActivationDateList.length === 0) {
            return;
        }

        if (dirtyFields.activationDate) {
            return;
        }

        const initialActivationAt = broadbandOffer?.activationAt;
        const matchFromCart = initialActivationAt
            ? filteredActivationDateList.find((date: Date) => isSameDay(date, initialActivationAt))
            : null;

        resetNoInstallForm({ activationDate: matchFromCart ?? firstAvailableDate });
    }, [
        appointmentType,
        broadbandOffer?.activationAt,
        dirtyFields.activationDate,
        error,
        filteredActivationDateList,
        firstAvailableDate,
        isLoading,
        resetNoInstallForm,
    ]);

    const installationInfoContent: InstallationInfoContent = useMemo(() => {
        if (prequal?.isPreOrder && prequal.serviceDate) {
            return { kind: "preorder", serviceDate: prequal.serviceDate };
        }
        if (appointmentType === EAppointmentType.NO_INSTALL) {
            return { kind: "no-install" };
        }

        return { kind: "none" };
    }, [appointmentType, prequal?.isPreOrder, prequal?.serviceDate]);

    const installationInfoTitle = useMemo(() => {
        if (prequal?.isPreOrder) {
            return t("preOrder");
        }
        if (appointmentType === EAppointmentType.NO_INSTALL) {
            return t("noInstall");
        }

        return undefined;
    }, [appointmentType, prequal?.isPreOrder, t]);

    const appointmentInitialValues = useMemo(() => {
        if (broadbandOffer?.appointment?.at && broadbandOffer?.appointment?.time) {
            return {
                appointmentDate: broadbandOffer.appointment.at,
                timeSlot: mapTimeSlotCodeToLabel(broadbandOffer.appointment.time),
            };
        }

        return undefined;
    }, [broadbandOffer?.appointment?.at, broadbandOffer?.appointment?.time]);

    const showBookInstallationAppointment = Boolean(
        offerId && !prequal?.isPreOrder && appointmentType === EAppointmentType.FULL_INSTALL
    );

    const showInstallationInfoFlowCard = Boolean(
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- booleans: false must not fall through to ??
        prequal?.isPreOrder || appointmentType === EAppointmentType.NO_INSTALL
    );

    return {
        appointmentInitialValues,
        appointmentType,
        classes,
        error,
        filteredActivationDateList,
        firstAvailableDate,
        getActivationDates,
        handleNextClick,
        installationInfoContent,
        installationInfoTitle,
        isLoading,
        isMobile,
        noInstallFormMethods,
        offerId,
        prequal,
        setAppointmentFormValues,
        showBookInstallationAppointment,
        showInstallationInfoFlowCard,
    };
};
